/*
    Copyright 2022 Dynatrace LLC

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import * as os from "os";

import { PROTO_VERSION } from "../../gen/proto/proto-version";
import { ARTIFACT_VERSION } from "../../gen/dynatrace/odin/semconv/v1/SemConvTrace";

import { ComponentLogger } from "../Logger";
import { getConfiguration } from "../configuration/StartupConfig";
import { prettyPrintObject } from "./LoggingUtils";
import { VERSIONS } from "../version";
import { toUint32, toUlong } from "./NumericUtils";

// ----------------------------------------------------------------------------
export function logStartupBanner(): void {
	const log = new ComponentLogger("Core");
	const config = getConfiguration();
	const { fullVersion, buildDate, commitHash, isDevVersion } = VERSIONS;

	log.info(`OneAgent ODIN Node.js version .. ${fullVersion}, build date ${buildDate}, SCM Rev. ${commitHash}`);

	if (isDevVersion) {
		const disclaimer = `This is a development version (${fullVersion}) and not intended for use in production environments`;
		if (log.enabled) {
			log.info(disclaimer);
		} else {
			// eslint-disable-next-line no-console
			console.error(`[Dynatrace] ${disclaimer}`);
		}
	}

	log.info(`Exporter ID .................... 0x${toUlong(config?.agentID)?.toString(16)}`);
	log.info(`Tenant ID ...................... 0x${toUint32(config?.tenantId)?.toString(16)}`);
	log.info(`Cluster ID ..................... 0x${toUint32(config?.clusterId)?.toString(16)}`);
	log.info(`Tenant UUID .................... ${config?.tenantUUID}`);
	log.info(`Connection URL ................. ${config?.baseUrl}`);
	log.info(`Process ID ..................... ${process.pid}`);
	log.info(`Platform ....................... ${os.platform()} - ${os.arch()} - ${os.release()}`);
	log.info(`Host ........................... ${os.hostname()}`);
	log.info(`Runtime ........................ ${process.release.name} - ${process.version}`);
	log.info(`Span processing interval ....... ${config?.spanProcessingIntervalMs}`);
	log.info(`Logging flags .................. ${prettyPrintObject(config?.loggingFlags ?? {})}`);
	log.info(`Rum ClientIp Headers ........... ${config?.rumClientIpHeaders}`);
	log.info(`Rum Application ID ............. ${config?.rumApplicationId}`);
	log.info(`Process arguments .............. [${process.argv.join(", ")}]`);
	log.info(`Special arguments .............. [${process.execArgv.join(", ")}]`);
	log.info(`NODE_OPTIONS ................... ${process.env.NODE_OPTIONS}`);
	log.info(`Main Module .................... ${require.main?.filename}`);
	log.info(`Protocol version ............... ${PROTO_VERSION}`);
	log.info(`SemConv version ................ ${ARTIFACT_VERSION}`);
}
